import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listGuides } from '../services/guidesService';
import { formatDate } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function GuidesPage() {
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const fetchList = useCallback(async () => {
    if (!ruc) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listGuides({ ruc, search, pageSize: 10 });
      setItems(result.items);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [ruc, search]);

  useEffect(() => {
    const t = setTimeout(fetchList, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [fetchList, search]);

  const columns = [
    {
      key: 'serie',
      label: 'Serie-N°',
      render: (r) => `${r.serie || '—'}-${r.correlativo ?? '—'}`,
    },
    { key: 'fecha', label: 'Fecha', render: (r) => formatDate(r.fechaEmision || r.createdAt) },
    {
      key: 'destinatario',
      label: 'Destinatario',
      render: (r) => r.destinatario?.rznSocial || r.rznSocial || r.destinatario?.numDoc || '—',
    },
    {
      key: 'estado',
      label: 'SUNAT',
      render: (r) => (
        <span>
          {r.aceptado ? <Badge tone="ok">Aceptado</Badge> : <Badge tone="warn">Pendiente</Badge>}
          {r.status ? <span className="muted"> {r.status}</span> : null}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) => (
        <button
          type="button"
          className="btn btn-mini"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/guias/${r.id}`);
          }}
        >
          Ver
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Guías de remisión</h1>
          <p className="muted">
            Colección <code>GR</code> (misma que usa la app). La emisión SUNAT se mantiene en el core.
          </p>
        </div>
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar serie o destinatario…" />
      </div>

      {loading ? (
        <LoadingState label="Cargando guías…" />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchList} />
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          keyField="id"
          onRowClick={(row) => navigate(`/admin/guias/${row.id}`)}
          empty={
            <EmptyState
              title="No hay guías"
              description="Se emiten desde la app móvil (GenerarGuia / SUNAT)."
            />
          }
        />
      )}
    </div>
  );
}
